'use client';

/**
 * Header toolbar that sits on top of "THE PROMPT" — shows char count, token
 * estimate, and a translate dropdown. Used by the detail page and the
 * detail modal so the two surfaces stay in sync.
 */
import { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { estimateTokens, formatTokens } from '@/lib/tokens';
import { useToast } from '@/lib/toast';

const TARGET_LANGUAGES: { code: string; label: string; flag: string }[] = [
  { code: 'en', label: 'English',   flag: '🇬🇧' },
  { code: 'th', label: 'ไทย',       flag: '🇹🇭' },
  { code: 'ja', label: '日本語',     flag: '🇯🇵' },
  { code: 'zh-CN', label: '中文 (简)', flag: '🇨🇳' },
  { code: 'ko', label: '한국어',     flag: '🇰🇷' },
  { code: 'es', label: 'Español',   flag: '🇪🇸' },
  { code: 'fr', label: 'Français',  flag: '🇫🇷' },
  { code: 'de', label: 'Deutsch',   flag: '🇩🇪' },
  { code: 'ru', label: 'Русский',   flag: '🇷🇺' },
  { code: 'vi', label: 'Tiếng Việt', flag: '🇻🇳' },
  { code: 'pt', label: 'Português', flag: '🇵🇹' },
  { code: 'ar', label: 'العربية',   flag: '🇸🇦' },
];


interface Props {
  /** The original (untranslated) prompt content. */
  text: string;
  /** Pre-existing label such as "THE PROMPT". Component renders to the right
   *  of whatever the parent puts in the label slot. */
  charCount?: number;
  /** Called when the user picks a translation; the parent should swap the
   *  rendered prompt block to `translated`. Pass null to revert. */
  onTranslated?: (translated: string | null, lang?: string) => void;
  /** Currently shown translation language (or null for the original). The
   *  toolbar uses this to render the active state on the dropdown trigger. */
  activeLang?: string | null;
}

export default function PromptToolbar({
  text, charCount, onTranslated, activeLang,
}: Props) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Token estimate is cheap — recompute on every text change.
  const tokens = estimateTokens(text);
  const chars = charCount ?? text.length;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  const pickLang = async (code: string) => {
    if (busy) return;
    setOpen(false);
    setBusy(true);
    try {
      const res = await api.translate.text(text, code);
      if (!res.translated || res.translated === text) {
        toast.info('Already in that language.');
        return;
      }
      onTranslated?.(res.translated, code);
      if (res.truncated) {
        toast.info('Translation truncated — prompt was longer than the upstream limit.');
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Translate failed';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const showOriginal = () => onTranslated?.(null);

  const activeLabel =
    activeLang ? (TARGET_LANGUAGES.find((l) => l.code === activeLang)?.label ?? activeLang) : null;

  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 10,
      flexWrap: 'wrap',
      fontSize: 11, color: 'var(--ink-3)',
      fontWeight: 500, letterSpacing: 'normal', textTransform: 'none',
    }}>
      {/* Char count */}
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>
        {chars.toLocaleString()} chars
      </span>

      <Dot />

      {/* Token estimate */}
      <span
        title="Approximate tokens (varies by model). Useful for guessing API cost."
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          padding: '2px 9px',
          background: 'oklch(0.95 0.04 75)',
          color: 'oklch(0.4 0.13 75)',
          borderRadius: 'var(--radius-full)',
          fontFamily: 'var(--font-mono)',
          fontSize: 10.5,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        ~{formatTokens(tokens)} tokens
      </span>

      <Dot />

      {/* Translate dropdown */}
      <div ref={wrapRef} style={{ position: 'relative' }}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          disabled={busy}
          aria-busy={busy || undefined}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '3px 10px',
            fontSize: 11, fontWeight: 500,
            background: activeLang ? 'oklch(0.96 0.05 285)' : 'transparent',
            border: `1px solid ${activeLang ? 'oklch(0.85 0.07 285)' : 'var(--line)'}`,
            color: activeLang ? 'oklch(0.4 0.16 285)' : 'var(--ink-2)',
            borderRadius: 'var(--radius-full)',
            cursor: busy ? 'wait' : 'pointer',
            transition: 'all 0.12s',
          }}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 13 }}>
            {busy ? 'progress_activity' : 'translate'}
          </span>
          {activeLabel
            ? `Translated · ${activeLabel}`
            : busy
              ? 'Translating…'
              : 'Translate'}
          <span className="material-symbols-outlined" style={{ fontSize: 12, opacity: 0.6 }}>
            arrow_drop_down
          </span>
        </button>
        {open && (
          <div
            role="listbox"
            style={{
              position: 'absolute', top: 'calc(100% + 6px)', right: 0,
              background: 'var(--bg-raised)',
              border: '1px solid var(--line)',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-lg)',
              padding: 4,
              minWidth: 180,
              maxHeight: 320,
              overflowY: 'auto',
              zIndex: 50,
            }}
          >
            <div style={{
              fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em',
              color: 'var(--ink-3)', padding: '6px 8px 4px', fontWeight: 600,
            }}>
              Translate to…
            </div>
            {TARGET_LANGUAGES.map((l) => (
              <button
                key={l.code}
                type="button"
                onClick={() => pickLang(l.code)}
                disabled={busy}
                aria-busy={busy || undefined}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  width: '100%', padding: '6px 10px',
                  fontSize: 13, fontWeight: 500,
                  textAlign: 'left',
                  background: activeLang === l.code ? 'var(--bg-soft)' : 'transparent',
                  color: 'var(--ink)',
                  border: 0,
                  borderRadius: 'var(--radius-sm)',
                  cursor: busy ? 'wait' : 'pointer',
                  font: 'inherit',
                }}
                onMouseEnter={(e) => {
                  if (activeLang !== l.code) e.currentTarget.style.background = 'var(--bg-soft)';
                }}
                onMouseLeave={(e) => {
                  if (activeLang !== l.code) e.currentTarget.style.background = 'transparent';
                }}
              >
                <span aria-hidden style={{ fontSize: 14 }}>{l.flag}</span>
                <span style={{ flex: 1 }}>{l.label}</span>
                {activeLang === l.code && (
                  <span className="material-symbols-outlined" style={{ fontSize: 14, color: 'var(--accent)' }}>
                    check
                  </span>
                )}
              </button>
            ))}
            {activeLang && (
              <>
                <div style={{
                  height: 1, background: 'var(--line-soft)', margin: '4px 6px',
                }} />
                <button
                  type="button"
                  onClick={() => { showOriginal(); setOpen(false); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    width: '100%', padding: '6px 10px',
                    fontSize: 13, fontWeight: 500,
                    color: 'var(--ink-2)', background: 'transparent',
                    border: 0, borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer', font: 'inherit',
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 14 }}>undo</span>
                  Show original
                </button>
              </>
            )}
            <div style={{
              fontSize: 10, color: 'var(--ink-3)',
              padding: '6px 8px 4px',
              borderTop: '1px solid var(--line-soft)',
              marginTop: 4,
            }}>
              Powered by Google Translate
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Dot() {
  return <span aria-hidden style={{ width: 3, height: 3, borderRadius: '50%', background: 'var(--ink-3)', opacity: 0.5 }} />;
}
